export type CommunicationIntegrationKind = "EMAIL" | "TWILIO" | "META_WHATSAPP";

export type IntegrationHealthStatus =
  | "UNKNOWN"
  | "HEALTHY"
  | "UNHEALTHY_AUTH"
  | "UNHEALTHY_CONNECTION";

export interface EmailSmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  fromAddress: string;
  fromName: string;
  replyToAddress: string;
}

export interface EmailImapConfig {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  inboxFolder: string;
}

export interface EmailIntegrationConfig {
  kind: "EMAIL";
  smtp: EmailSmtpConfig;
  imap: EmailImapConfig | null;
}

export interface TwilioIntegrationConfig {
  kind: "TWILIO";
  accountSid: string;
  authToken: string;
  smsPhoneNumber: string | null;
}

export interface MetaWhatsappIntegrationConfig {
  kind: "META_WHATSAPP";
  accessToken: string;
  phoneNumberId: string;
  wabaId: string;
  displayPhoneNumber: string;
  appSecret: string;
  webhookVerifyToken: string;
  graphApiVersion: string;
}

export type CommunicationIntegrationConfig =
  | EmailIntegrationConfig
  | TwilioIntegrationConfig
  | MetaWhatsappIntegrationConfig;

export interface CommunicationIntegration {
  id: string;
  hotelId: string;
  kind: CommunicationIntegrationKind;
  enabled: boolean;
  healthStatus: IntegrationHealthStatus;
  lastError: string | null;
  lastVerifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  config: CommunicationIntegrationConfig;
}

export interface IntegrationHealthProbeResult {
  status: Exclude<IntegrationHealthStatus, "UNKNOWN">;
  error: string | null;
}

export type WhatsAppSystemFlowKey = "PRE_CHECKIN" | "PRE_CHECKOUT";

export type WhatsAppSystemFlowRegistryStatus =
  | "MISSING"
  | "CONFIGURED"
  | "READ_FAILED";

export interface WhatsAppFlowEncryptionStatus {
  publicKeyConfiguredInEnv: boolean;
  privateKeyConfiguredInEnv: boolean;
  metaPublicKeyConfigured: boolean;
  metaSignatureStatus?: string;
  error?: string;
}

export interface WhatsAppSystemFlowStatusItem {
  key: WhatsAppSystemFlowKey;
  configured: boolean;
  status: WhatsAppSystemFlowRegistryStatus;
  flowId?: string | null;
  flowName?: string | null;
  flowStatus?: string | null;
  previewUrl?: string | null;
  validationErrors?: unknown;
  error?: string;
}

export interface WhatsAppFlowOperationsStatus {
  hotelId: string;
  phoneNumberId: string;
  wabaId: string;
  endpointUri?: string;
  flowAction: "data_exchange" | "navigate";
  encryption: WhatsAppFlowEncryptionStatus;
  flows: WhatsAppSystemFlowStatusItem[];
}

export type WhatsAppBotReadinessStatus = "READY" | "ACTION_REQUIRED" | "FAILED";

export type WhatsAppBotReadinessCheckStatus = "OK" | "WARNING" | "FAILED";

export type WhatsAppBotReadinessCheckKey =
  | "INTEGRATION"
  | "PHONE_NUMBER"
  | "WHATSAPP_CHANNEL"
  | "CHANNEL_PROVIDER"
  | "WEBHOOK_SUBSCRIPTION"
  | "FLOW_ENDPOINT"
  | "SYSTEM_FLOWS"
  | "BOT_RUNTIME_CONFIG"
  | "LIFECYCLE_TEMPLATES";

export interface WhatsAppBotReadinessCheck {
  key: WhatsAppBotReadinessCheckKey;
  status: WhatsAppBotReadinessCheckStatus;
  message: string;
  details?: Record<string, unknown>;
}

export interface WhatsAppBotReadinessStatusResponse {
  status: WhatsAppBotReadinessStatus;
  hotelId: string;
  phoneNumberId?: string;
  wabaId?: string;
  webhookCallbackUri?: string;
  flowEndpointUri?: string;
  checks: WhatsAppBotReadinessCheck[];
  blockingIssues: string[];
  warnings: string[];
}

export interface SyncWhatsAppSystemFlowsRequest {
  replaceExisting?: boolean;
  configureEncryption?: boolean;
}

export interface SyncWhatsAppSystemFlowsResponse {
  hotelId: string;
  endpointUri?: string;
  encryptionStep?: {
    name: string;
    status: "OK" | "SKIPPED" | "FAILED";
    message?: string;
  };
  systemFlows: {
    created: number;
    skipped: number;
    items: Array<{
      key: WhatsAppSystemFlowKey;
      status: "CREATED" | "SKIPPED";
      flowId?: string;
      flowName?: string;
      reason?: string;
    }>;
  };
}

export interface UpsertEmailIntegrationSmtpRequest {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password?: string | null;
  fromAddress: string;
  fromName: string;
  replyToAddress: string;
}

export interface UpsertEmailIntegrationImapRequest {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password?: string | null;
  inboxFolder: string;
}

export interface UpsertEmailIntegrationRequest {
  enabled?: boolean;
  smtp: UpsertEmailIntegrationSmtpRequest;
  imap?: UpsertEmailIntegrationImapRequest | null;
}

export interface UpsertTwilioIntegrationRequest {
  enabled?: boolean;
  accountSid: string;
  authToken?: string | null;
  smsPhoneNumber: string | null;
}

export interface UpsertMetaWhatsappIntegrationRequest {
  enabled?: boolean;
  accessToken?: string | null;
  phoneNumberId: string;
  wabaId: string;
  displayPhoneNumber: string;
  appSecret?: string | null;
  webhookVerifyToken?: string | null;
  graphApiVersion: string;
  phoneRegistrationPin?: string;
}

export type UpsertIntegrationRequest =
  | UpsertEmailIntegrationRequest
  | UpsertTwilioIntegrationRequest
  | UpsertMetaWhatsappIntegrationRequest;

export interface ToggleIntegrationRequest {
  enabled: boolean;
}

export const COMMUNICATION_INTEGRATION_KINDS: readonly CommunicationIntegrationKind[] =
  ["EMAIL", "TWILIO", "META_WHATSAPP"] as const;

export const KIND_TO_LINKED_CHANNEL: Record<
  CommunicationIntegrationKind,
  "EMAIL" | "SMS" | "WHATSAPP"
> = {
  EMAIL: "EMAIL",
  TWILIO: "SMS",
  META_WHATSAPP: "WHATSAPP",
};
